/**
 * Dev-only debug badge that shows the most recent spam-check result for a form.
 * Renders nothing in production builds (import.meta.env.DEV is false).
 *
 * Usage:
 *   const { checkSpam, lastResult, debug } = useSpamProtection();
 *   ...
 *   <SpamDebugBadge result={lastResult} enabled={debug} />
 */
import type { SpamCheckResult } from "@/hooks/useSpamProtection";

const SpamDebugBadge = ({
  result,
  enabled,
}: {
  result: SpamCheckResult | null;
  enabled: boolean;
}) => {
  if (!enabled || !result) return null;

  const tone = result.isSpam
    ? "border-destructive/40 bg-destructive/10 text-destructive"
    : "border-primary/30 bg-primary/5 text-primary";

  return (
    <div
      className={`text-xs font-mono rounded-md border px-3 py-2 ${tone}`}
      data-testid="spam-debug-badge"
      role="status"
      aria-live="polite"
    >
      <div>
        <strong>[dev] spam check:</strong>{" "}
        {result.isSpam ? `BLOCKED (${result.reason})` : "passed"}
      </div>
      <div className="opacity-70">elapsed: {result.elapsedMs}ms</div>
      {result.matchedPattern && (
        <div className="opacity-70 break-all">spam match: {result.matchedPattern}</div>
      )}
      {result.matchedWhitelist && (
        <div className="opacity-70 break-all">whitelist match: {result.matchedWhitelist}</div>
      )}
      {result.matchedLearned && (
        <div className="opacity-70">passed via learned allowlist</div>
      )}
    </div>
  );
};

export default SpamDebugBadge;
